import React, { FormEvent } from 'react';
import { RouteComponentProps } from 'react-router';
import { connect } from 'react-redux';
import { ThunkDispatch } from 'redux-thunk';
import { Link } from 'react-router-dom';
import { AppState } from '../../../../store/store.types';

import {
  onRequestRuleEditRoles,
  OnRequestRuleFilters,
  OnRequestRuleModel
} from '../../../../api/pricing/pricing.types';
import Select from '../../../../components/forms/Select';
import { CarClassModel, DeskModel } from '../../../../api/core/core.types';
import { getDeskList } from '../../../setup/actions/desk.actions';
import { UserModel } from '../../../../api/authentication/authentication.types';
import { userCanAccessComponent } from '../../../../helpers/authentication.helper';
import { defaultPageSize, PageRequest } from '../../../../api/api.types';
import { getOnRequestRuleList, updateOnRequestRuleFilters } from '../../actions/onrequestrule.actions';
import { OnRequestRuleActionTypes } from '../../types/onrequestrule.types';
import StatusLabel from '../../../../components/forms/StatusLabel';
import { WithTranslation, withTranslation } from 'react-i18next';
import { getAllCarClasses } from '../../../core/actions/carClass.actions';
import { formatDateTime } from '../../../../helpers/date.helper';
import ListViewTable from '../../../../components/tables/ListViewTable';
import { UncontrolledTooltip as Tooltip } from 'reactstrap';

interface OnRequestRulesProps extends RouteComponentProps, WithTranslation {
  getOnRequestRulesList: (filters?: OnRequestRuleFilters, page?: PageRequest) => void;
  getAllCarClasses: () => void;
  getDeskList: () => void;
  updateFilters: (filters: OnRequestRuleFilters) => void;
  onRequestRules: OnRequestRuleModel[] | null;
  desks: DeskModel[];
  carClasses: CarClassModel[];
  user: UserModel | null;
  totalPages: number;
  totalElements: number;
  filters: OnRequestRuleFilters | null;
}

interface OnRequestRulesState {
  selectedPageNumber: number;
  sortOrder: 'ASC' | 'DESC';
  sortedBy: string;
  filtersUpdated: boolean;
  filter: {
    deskFilter: {
      value: { label: string; value: string }[];
    };
    carClassFilter: {
      value: { label: string; value: string }[];
    };
  };
}

const OnRequestRuleSortFields = [
  { value: 'validFrom', label: 'validFrom' }
];

class OnRequestRules extends React.Component<OnRequestRulesProps, OnRequestRulesState> {
  constructor(props: OnRequestRulesProps) {
    super(props);
    this.state = {
      selectedPageNumber: 1,
      sortOrder: 'DESC',
      sortedBy: 'id',
      filtersUpdated: false,
      filter: {
        deskFilter: {
          value: []
        },
        carClassFilter: {
          value: []
        }
      }
    };
  }

  componentDidMount() {
    this.props.getAllCarClasses();
    this.props.getDeskList();
    this.props.getOnRequestRulesList();
  }

  componentDidUpdate() {
    if (!this.state.filtersUpdated) this.updateFilterValues();
  }

  updateFilterValues = () => {
    this.setState({ filtersUpdated: true });
    const filters = this.props.filters;
    if (filters) {
      const desks = filters.desk ? filters.desk.split(',') : undefined;
      const cars = filters.carClass ? filters.carClass.split(',') : undefined;

      const selectedDeskOptions: { value: string; label: string }[] = [];
      if (desks && desks.length) {
        desks.forEach((sd) => {
          const desk = this.props.desks.find((d) => d.code === sd);
          if (desk) selectedDeskOptions.push({ value: desk.code, label: desk.name });
        });
      }

      const selectedCarClassOptions: { value: string; label: string }[] = [];
      if (cars && cars.length) {
        cars.forEach((sc) => {
          selectedCarClassOptions.push({ value: sc, label: sc });
        });
      }

      this.setState({
        selectedPageNumber: filters.pageNumber ? filters.pageNumber : 1,
        filtersUpdated: true,
        filter: {
          deskFilter: {
            value: selectedDeskOptions
          },
          carClassFilter: {
            value: selectedCarClassOptions
          }
        }
      });
    }
  };

  clearFilters = () => {
    this.setState({
      selectedPageNumber: 1,
      filtersUpdated: true,
      filter: {
        deskFilter: {
          value: []
        },
        carClassFilter: {
          value: []
        }
      }
    });

    const filters = this.composeFilters();
    this.props.updateFilters(filters);
    this.props.getOnRequestRulesList();
  };

  onRowClick = (rowInfo: any) => this.props.history.push(`/rates/onrequestrules/${rowInfo.original.id}/view`);

  carClassCell = ({ row }: { row: any }) => {
    const { t } = this.props;

    if (row.original.onRequestRuleCarClasses.length > 0) {
      const carList: JSX.Element[] = [];
      row.original.onRequestRuleCarClasses.forEach((carClass: any) => {
        const carObj = this.props.carClasses.find((c) => c.acrissCode === carClass.acrissCode);
        carList.push(
          <div
            key={carClass.acrissCode}
            className="flex-50 px-1"
            style={{ maxWidth: '60px' }}
          >
            {carObj ? carObj.acrissCode : carClass.acrissCode}
          </div>
        );
      });
      if (row.original.onRequestRuleCarClasses.length === 1) {
        const carObj = this.props.carClasses.find((d) => d.acrissCode === row.original.onRequestRuleCarClasses[ 0 ].acrissCode);
        return carObj && carObj.acrissCode || row.original.onRequestRuleCarClasses[ 0 ].acrissCode;
      }
      return (
        <div>
          <div
            className="btn btn-link font-normal p-0"
            id={`cars-tooltip-${row.original.id}`}
          >
            {t('v.car_classes', { count: row.original.onRequestRuleCarClasses.length })}
          </div>
          <Tooltip
            modifiers={{ preventOverflow: { boundariesElement: 'window' } }}
            placement="top"
            target={`cars-tooltip-${row.original.id}`}
            autohide={false}
          >
            {carList}
          </Tooltip>
        </div>
      );
    }
    return t('v.all_car_classes');
  };

  deskCell = ({ row }: { row: any }) => {
    const { t } = this.props;

    if (row.original.onRequestRuleDesks.length > 0) {
      const deskList: JSX.Element[] = [];
      row.original.onRequestRuleDesks.forEach((desk: any) => {
        const deskObj = this.props.desks.find((d) => d.code === desk.deskCode);
        deskList.push(<div key={desk.deskCode}>{deskObj ? deskObj.name : desk.deskCode}</div>);
      });

      if (row.original.onRequestRuleDesks.length === 1) {
        const deskObj = this.props.desks.find((d) => d.code === row.original.onRequestRuleDesks[ 0 ].deskCode);
        return deskObj && deskObj.name || row.original.onRequestRuleDesks[ 0 ].deskCode;
      }
      return (
        <div>
          <div
            className="btn btn-link font-normal p-0"
            id={`desks-tooltip-${row.original.id}`}
          >
            {t('v.desks', { count: row.original.onRequestRuleDesks.length })}
          </div>
          <Tooltip
            modifiers={{ preventOverflow: { boundariesElement: 'window' } }}
            placement="top"
            target={`desks-tooltip-${row.original.id}`}
            autohide={false}
          >
            {deskList}
          </Tooltip>
        </div>
      );
    }
    return t('v.all_desks');
  };

  statusCell = (props: any) => (<StatusLabel status={props}/>);

  onFilterClick = (e: FormEvent) => {
    e.preventDefault();
    const filters = this.composeFilters();
    this.props.getOnRequestRulesList(filters);
    this.props.updateFilters(filters);
  };

  handleUpdateResults = () => {
    const filters = this.composeFilters();
    this.props.getOnRequestRulesList(filters);
    this.props.updateFilters(filters);
  };

  composeFilters = () => {
    const deskFilterValue: any = this.state.filter.deskFilter.value;
    const carClassFilterValue: any = this.state.filter.carClassFilter.value;

    const filters: OnRequestRuleFilters = {
      desk: deskFilterValue.value,
      carClass: carClassFilterValue.value,
      pageNumber: this.state.selectedPageNumber - 1
    };

    return filters;
  };

  validFromCell = (props: any) => {
    if (props.row.original.validFrom.length > 16) {
      return (
        <div className="numeric-content">
          {`${formatDateTime(props.row.original.validFrom)} - ${formatDateTime(props.row.original.validTo)}`}
        </div>
      );
    }
  };

  changeHandler = (event: { target: HTMLInputElement }) => {
    const { name, value } = event.target;
    this.setState({
      filter: {
        ...this.state.filter,
        [ name ]: {
          value
        }
      }
    });
  };

  dropdownChange = (newValue: any, field: string) => {
    this.setState({
      filter: {
        ...this.state.filter,
        [ field ]: {
          value: newValue
        }
      }
    }, () => {
      this.handleUpdateResults();
    });
  };

  defineColumns() {
    const { t } = this.props;

    return [
      {
        header: t('ct.on_request_rules.period'),
        accessorKey: 'validFrom',
        cell: ({ row }: { row: any }) => this.validFromCell({ row }),
        meta: { align: 'right', maxWidth:250, numeric: true }
      },
      {
        header: t('ct.on_request_rules.car_classes'),
        accessorKey: 'onRequestRuleCarClasses',
        cell: ({ row }: { row: any }) => this.carClassCell({ row })
      },
      {
        header: t('ct.on_request_rules.desks'),
        accessorKey: 'onRequestRuleDesks',
        cell: ({ row }: { row: any }) => this.deskCell({ row })
      },
      {
        header: t('ct.on_request_rules.status'),
        accessorKey: 'statusType',
        cell: (info: any) => this.statusCell(info.getValue())
      }
    ];
  }

  sortBy = (columnName: string, sortDirection: 'ASC' | 'DESC') => {
    const sortFieldName = OnRequestRuleSortFields.find((column) => column.value === columnName);
    if (!sortFieldName) {
      return;
    }
    this.setState({ sortOrder: sortDirection, sortedBy: sortFieldName.label });
    return (
      this.props.getOnRequestRulesList(this.composeFilters(), {
        page: this.state.selectedPageNumber - 1,
        size: defaultPageSize,
        sortField: sortFieldName.label,
        sortDirection: sortDirection
      })
    );
  };

  getDataFn(pageRequest: PageRequest) {
    if (pageRequest.page !== undefined && pageRequest.sortDirection !== undefined) {
      const pageRequestFix = {
        page: pageRequest.page,
        sortDirection: pageRequest.sortDirection,
        sortField: OnRequestRuleSortFields.find((column) => column.label === pageRequest.sortField)?.label ?? 'id'
      };
      this.props.getOnRequestRulesList(this.composeFilters(), pageRequestFix);

      this.setState({
        selectedPageNumber: pageRequestFix.page + 1,
        sortOrder: pageRequestFix.sortDirection ?? 'ASC',
        sortedBy: OnRequestRuleSortFields.find((column) => column.label === pageRequest.sortField)?.label ?? 'id'
      });
    }
  }

  render() {
    const { t } = this.props;

    return (
      <main>
        <div className="page-content">
          <div className="d-flex justify-content-between align-items-center">
            <h1>{t('page_title.on_request_rules')}</h1>
            {this.props.user && userCanAccessComponent(this.props.user, onRequestRuleEditRoles) && (
              <Link
                to={{
                  pathname: '/rates/onrequestrules/create'
                }}
                className="btn btn-primary btn-wide"
              >
                {t('b.add_new').toUpperCase()}
              </Link>
            )}
          </div>
          <form
            className="d-flex justify-content-between container-fluid filter-container form-group form-inline flex-wrap">
            <div className="d-flex row ml-1 filters-container-margin-top-btm">
              <Select
                value={this.state.filter.carClassFilter.value}
                className="mr-1"
                equalWidthElement
                elWidth={240}
                placeholder={t('ph.filter.on_request_rules.by_car_class')}
                // eslint-disable-next-line no-nested-ternary
                options={this.props.carClasses.sort((a, b) => ((a.acrissCode < b.acrissCode) ? -1 : (a.acrissCode > b.acrissCode) ? 1 : 0)).map((m) => ({
                  label: m.acrissCode,
                  value: m.acrissCode
                }))}
                onChange={(val) => this.dropdownChange(val, 'carClassFilter')}
                closeMenuOnSelect={false}
                hideSelectedOptions={false}
                label="car class"
              />
              <Select
                value={this.state.filter.deskFilter.value}
                className="mr-1"
                placeholder={t('ph.filter.on_request_rules.by_desk')}
                equalWidthElement
                elWidth={240}
                // eslint-disable-next-line no-nested-ternary
                options={this.props.desks.sort((a, b) => ((a.name < b.name) ? -1 : (a.name > b.name) ? 1 : 0)).map((d) => ({
                  label: d.name,
                  value: d.code
                }))}
                onChange={(val) => this.dropdownChange(val, 'deskFilter')}
                closeMenuOnSelect={false}
                hideSelectedOptions={false}
                label="location"
              />
            </div>
            <div className="pl-1 d-flex justify-content-end">
              <button type="button" className="btn btn-text-only mr-2" onClick={() => this.clearFilters()}>
                {t('b.clear_filter')}</button>
              <button type="submit" className="btn btn-primary filter-button" onClick={this.onFilterClick}>
                {t('b.filter').toUpperCase()}</button>
            </div>
          </form>
          {this.props.onRequestRules
            ? (
              <div className="table-list">
                <ListViewTable
                  data={this.props.onRequestRules}
                  columns={this.defineColumns()}
                  getTrProps={this.onRowClick}
                  sortBy={this.sortBy}
                  sortOrder={this.state.sortOrder}
                  sortedBy={OnRequestRuleSortFields.find(({ value, label }) => this.state.sortedBy === label)?.value}
                  paginationEnabled={true}
                  totalPages={this.props.totalPages}
                  totalElements={this.props.totalElements}
                  selectedPageNumber={this.state.selectedPageNumber}
                  getDataFn={(pageRequest: PageRequest) => this.getDataFn(pageRequest)}
                />
              </div>
            ) : null}
        </div>
      </main>
    );
  }
}

const mapStateToProps = (state: AppState) => ({
  onRequestRules: state.rates.onRequestRules.onRequestRules,
  carClasses: state.core.carClasses.acrissCodes,
  desks: state.setup.desks.desks,
  user: state.authentication.user,
  totalPages: state.rates.onRequestRules.totalPages,
  totalElements: state.rates.onRequestRules.totalElements,
  filters: state.rates.onRequestRules.filters
});

const mapDispatchToProps = (dispatch: ThunkDispatch<{}, {}, OnRequestRuleActionTypes>) => ({
  getOnRequestRulesList: (filters?: OnRequestRuleFilters, pageRequest?: PageRequest) => dispatch(getOnRequestRuleList(filters, pageRequest)),
  getAllCarClasses: () => dispatch(getAllCarClasses()),
  getDeskList: () => dispatch(getDeskList()),
  updateFilters: (filters: OnRequestRuleFilters) => dispatch(updateOnRequestRuleFilters(filters))
});
export default withTranslation()(connect(
  mapStateToProps,
  mapDispatchToProps
)(OnRequestRules));
